import React from 'react';
import * as Yup from 'yup';
import { Modal, ModalActions, ModalContent, Alert, CancelButton, SubmitButton, TextInput, useAlert } from '@uplift/ui-v2';
import { Box, Typography } from '@mui/material';
import { useUserQuery } from '@uplift/queries';
import { PatientUploadedMedicalDocument, deletePatientMedicalDocumentRecord } from '@uplift/api';
import { Formik } from 'formik';
import { useL10n } from '@uplift/hooks';


export const DocumentDeleteModal: React.VFC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  document?: PatientUploadedMedicalDocument;
}> = (props) => {
  const l10n = useL10n();
  const alert = useAlert();
  const { data: patient } = useUserQuery(props.document?.patientId);

  const documentDeleteValidationSchema = React.useMemo(() => Yup.object().shape({
    reasonForDelete: Yup.string().required('A deletion reason is required'),
  }), []);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        id: props.document?.id,
        reasonForDelete: '',
      }}
      validateOnMount
      validationSchema={documentDeleteValidationSchema}
      onSubmit={async (values) => {
        if (!props.document) {
          return;
        }

        try {
          await deletePatientMedicalDocumentRecord(props.document.patientId, props.document.id, values.reasonForDelete);
          await props.onSuccess();
          alert.success('Document deleted');
        } catch (err) {
          alert.error(<>There was a problem deleting the document. Try again or contact <strong>{l10n.helpEmail}</strong></>);
          throw err;
        }
      }}
    >
      <Modal
        open={props.open}
        onClose={props.onClose}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: { style: { maxWidth: '600px' } }
        }}
        title="Delete document"
      >
        <ModalContent>
          <Alert severity="warning">
            <Typography>
              Deleting will remove this document from {patient?.firstName}’s chart and this action cannot be undone.
            </Typography>
          </Alert>

          <Box mt={3} width="100%">
            <TextInput
              name="reasonForDelete"
              label="Why are you deleting this document?"
              color="secondary"
              multiline
              minRows={1}
              fullWidth
            />
          </Box>
        </ModalContent>
        <ModalActions>
          <CancelButton onClick={props.onClose}>Cancel</CancelButton>
          <SubmitButton color="error">Delete document</SubmitButton>
        </ModalActions>
      </Modal>
    </Formik>
  );
};
